import { appStorage } from '../../lib/betaStorage'
import type { CareMenuData, DietaryProfile, MealProgress, MenuAssignment, MenuTemplate, RecipeNotes, RecipeSummary } from './types'

export interface CareMenuRepository {
  read(participantId: string): Promise<CareMenuData>
  assignMeal(participantId: string, date: string, meal: RecipeSummary, actorUserId: string): Promise<CareMenuData>
  clearMeal(participantId: string, date: string): Promise<CareMenuData>
  saveTemplate(participantId: string, template: MenuTemplate): Promise<CareMenuData>
  deleteTemplate(participantId: string, templateId: string): Promise<CareMenuData>
  applyTemplate(participantId: string, weekDates: string[], templateId: string, actorUserId: string): Promise<CareMenuData>
  saveRecipeNotes(participantId: string, recipeId: string, notes: RecipeNotes): Promise<CareMenuData>
  saveDietaryProfile(participantId: string, profile: DietaryProfile): Promise<CareMenuData>
  saveProgress(participantId: string, progressKey: string, progress: MealProgress): Promise<CareMenuData>
}

const STORE_PREFIX = 'marnixia.careMenu.v1'
const CHANGE_EVENT = 'marnixia:care-menu-change'

const emptyDietaryProfile = (): DietaryProfile => ({
  allergies: '', intolerances: '', dietaryPreferences: '', textureRequirements: '', safetyNotes: '',
})

function emptyData(participantId: string): CareMenuData {
  return { schemaVersion: 1, participantId, assignments: {}, templates: [], recipeNotes: {}, dietaryProfile: emptyDietaryProfile(), progress: {} }
}

function keyFor(participantId: string) { return `${STORE_PREFIX}.${participantId}` }

function parseData(participantId: string, raw: string | null): CareMenuData {
  if (!raw) return emptyData(participantId)
  try {
    const parsed = JSON.parse(raw) as Partial<CareMenuData>
    if (parsed.schemaVersion !== 1 || parsed.participantId !== participantId) return emptyData(participantId)
    return {
      ...emptyData(participantId), ...parsed,
      assignments: parsed.assignments ?? {}, templates: parsed.templates ?? [], recipeNotes: parsed.recipeNotes ?? {},
      dietaryProfile: { ...emptyDietaryProfile(), ...(parsed.dietaryProfile ?? {}) }, progress: parsed.progress ?? {},
    }
  } catch { return emptyData(participantId) }
}

function persist(data: CareMenuData) {
  appStorage.setItem(keyFor(data.participantId), JSON.stringify(data))
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { participantId: data.participantId } }))
  return data
}

function load(participantId: string) { return parseData(participantId, appStorage.getItem(keyFor(participantId))) }

export const localCareMenuRepository: CareMenuRepository = {
  async read(participantId) { return load(participantId) },
  async assignMeal(participantId, date, meal, actorUserId) {
    const data = load(participantId)
    const assignment: MenuAssignment = { date, meal, assignedAt: new Date().toISOString(), assignedByUserId: actorUserId }
    return persist({ ...data, assignments: { ...data.assignments, [date]: assignment } })
  },
  async clearMeal(participantId, date) {
    const data = load(participantId); const assignments = { ...data.assignments }; delete assignments[date]
    return persist({ ...data, assignments })
  },
  async saveTemplate(participantId, template) {
    const data = load(participantId)
    const templates = data.templates.some(item => item.id === template.id) ? data.templates.map(item => item.id === template.id ? template : item) : [...data.templates, template]
    return persist({ ...data, templates })
  },
  async deleteTemplate(participantId, templateId) {
    const data = load(participantId); return persist({ ...data, templates: data.templates.filter(item => item.id !== templateId) })
  },
  async applyTemplate(participantId, weekDates, templateId, actorUserId) {
    const data = load(participantId); const template = data.templates.find(item => item.id === templateId)
    if (!template) return data
    const assignments = { ...data.assignments }
    weekDates.forEach((date, index) => { const meal = template.mealsByWeekday[String(index)]; if (meal) assignments[date] = { date, meal, assignedAt: new Date().toISOString(), assignedByUserId: actorUserId } })
    return persist({ ...data, assignments })
  },
  async saveRecipeNotes(participantId, recipeId, notes) {
    const data = load(participantId); return persist({ ...data, recipeNotes: { ...data.recipeNotes, [recipeId]: notes } })
  },
  async saveDietaryProfile(participantId, dietaryProfile) {
    const data = load(participantId); return persist({ ...data, dietaryProfile })
  },
  async saveProgress(participantId, progressKey, progress) {
    const data = load(participantId); return persist({ ...data, progress: { ...data.progress, [progressKey]: progress } })
  },
}

export function subscribeToCareMenu(participantId: string, callback: () => void) {
  const onChange = (event: Event) => { const detail = (event as CustomEvent).detail; if (!detail?.participantId || detail.participantId === participantId) callback() }
  window.addEventListener(CHANGE_EVENT, onChange); window.addEventListener('storage', onChange)
  return () => { window.removeEventListener(CHANGE_EVENT, onChange); window.removeEventListener('storage', onChange) }
}


